import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { Plus, BookOpen, ChevronRight, X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMOTION_OPTIONS = ["Joie", "Peur", "Surprise", "Tristesse", "Confusion", "Paix", "Espoir", "Anxiété"];
const TAG_OPTIONS = ["Eau", "Ciel", "Animal", "Personne", "Lumière", "Obscurité", "Vol", "Voyage", "Maison", "Nature", "Prière", "Mort"];

export default function JournalPage() {
  const { api } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [dreams, setDreams] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [emotions, setEmotions] = useState([]);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    loadDreams();
  }, []);

  const loadDreams = async () => {
    try {
      const res = await api().get("/dreams");
      setDreams(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  };

  const toggleItem = (list, setList, item) => {
    if (list.includes(item)) setList(list.filter((i) => i !== item));
    else setList([...list, item]);
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    try {
      await api().post("/dreams", { title, content, emotions, tags });
      setTitle("");
      setContent("");
      setEmotions([]);
      setTags([]);
      setShowForm(false);
      loadDreams();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white drop-shadow-lg">Journal des rêves</h1>
            <p className="text-slate-300/80 text-sm">{dreams.length} rêve{dreams.length !== 1 ? "s" : ""} enregistré{dreams.length !== 1 ? "s" : ""}</p>
          </div>
          <button
            data-testid="journal-add-button"
            onClick={() => setShowForm(!showForm)}
            className="w-10 h-10 rounded-full bg-amber-500/90 flex items-center justify-center shadow-lg hover:bg-amber-500 transition-all active:scale-95"
          >
            {showForm ? <X size={18} className="text-white" /> : <Plus size={18} className="text-white" />}
          </button>
        </div>

        {/* New Dream Form */}
        {showForm && (
          <div data-testid="journal-new-form" className="glass-card p-5 mb-6 animate-fadeInUp">
            <p className="text-[10px] text-amber-400 uppercase tracking-[0.15em] font-semibold mb-4">Nouveau rêve</p>
            <input
              data-testid="journal-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre du rêve"
              className="w-full bg-slate-950/40 border border-white/10 text-white placeholder:text-slate-500 rounded-xl h-12 px-4 mb-3 focus:border-amber-500/50 outline-none transition-all text-sm"
            />
            <textarea
              data-testid="journal-content-input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Décrivez votre rêve en détail..."
              className="w-full bg-slate-950/40 border border-white/10 text-white placeholder:text-slate-500 rounded-xl p-4 min-h-[120px] mb-4 focus:border-amber-500/50 outline-none transition-all resize-none text-sm"
            />

            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Émotions</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {EMOTION_OPTIONS.map((em) => (
                <button
                  key={em}
                  data-testid={`emotion-${em.toLowerCase()}`}
                  onClick={() => toggleItem(emotions, setEmotions, em)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    emotions.includes(em)
                      ? "bg-amber-500/80 text-white"
                      : "bg-white/5 text-slate-400 hover:bg-white/10"
                  }`}
                >
                  {em}
                </button>
              ))}
            </div>

            <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Thèmes</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {TAG_OPTIONS.map((tag) => (
                <button
                  key={tag}
                  data-testid={`tag-${tag.toLowerCase()}`}
                  onClick={() => toggleItem(tags, setTags, tag)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    tags.includes(tag)
                      ? "bg-purple-500/80 text-white"
                      : "bg-white/5 text-slate-400 hover:bg-white/10"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            <button
              data-testid="journal-save-button"
              onClick={handleSave}
              disabled={saving || !title.trim() || !content.trim()}
              className="w-full btn-gold py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : null}
              {saving ? "Sauvegarde..." : "Enregistrer le rêve"}
            </button>
          </div>
        )}

        {/* Dream List */}
        {loadingList ? (
          <div className="flex justify-center py-10">
            <Loader2 size={24} className="animate-spin text-amber-400" />
          </div>
        ) : dreams.length === 0 ? (
          <div data-testid="journal-empty" className="text-center py-16 animate-fadeIn">
            <BookOpen size={48} className="text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-sm">Aucun rêve enregistré</p>
            <p className="text-slate-500 text-xs mt-1">Commencez par ajouter votre premier rêve</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dreams.map((dream, i) => (
              <button
                key={dream.id}
                data-testid={`dream-card-${i}`}
                onClick={() => navigate(`/journal/${dream.id}`)}
                className="w-full glass-card p-4 text-left hover:scale-[1.01] transition-all animate-fadeInUp flex items-center gap-4"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{dream.title}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{dream.content}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] text-slate-500">{formatDate(dream.created_at)}</span>
                    {dream.emotions?.slice(0, 2).map((em) => (
                      <span key={em} className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">{em}</span>
                    ))}
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-500 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
      <BottomNav />
    </AppLayout>
  );
}
